import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useClient, useContacts } from "@/modules/clients/api";
import { useTaxRegimes } from "@/modules/settings/SettingsPage";
import { DocumentEditor, emptyLine, type EditorDoc } from "@/modules/documents/DocumentEditor";
import { useQuote, useSaveQuoteDraft, quoteToDoc, type QuoteHeader } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d: string, n: number) => new Date(new Date(d).getTime() + n * 86_400_000).toISOString().slice(0, 10);

/** Création (/devis/nouveau?client=…) et modification d'un brouillon (/devis/:id/modifier). */
export default function QuoteEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const existing = useQuote(id);
  const [clientId, setClientId] = useState(params.get("client") ?? "");
  const { data: client } = useClient(clientId || undefined);
  const { data: contacts } = useContacts(clientId || undefined);
  const { data: regimes } = useTaxRegimes(true);
  const { data: settings } = useQuery({ queryKey: ["settings", "defaults"], queryFn: async () => { const { data, error } = await supabase.from("settings").select("value").eq("key", "defaults").single(); if (error) throw error; return data.value as { quote_validity_days?: number; deposit_pct?: number; payment_days?: number }; } });
  const { data: clientsList } = useQuery({ queryKey: ["clients", "pick"], queryFn: async () => { const { data, error } = await supabase.from("clients").select("id, legal_name, currency").is("deleted_at", null).eq("status", "active").order("legal_name"); if (error) throw error; return data; } });
  const save = useSaveQuoteDraft();

  const [header, setHeader] = useState<QuoteHeader>({ client_id: "", contact_id: null, title: "", issue_date: today(), valid_until: null, payment_days: 30, tax_regime_id: null, tax_label: null, tax_rate_pct: 0, tax_mention: null, notes_client: "", notes_internal: "", terms: {} });
  const [doc, setDoc] = useState<EditorDoc>({ currency: "EUR", lines: [emptyLine()], fees: [], global_discount_type: null, global_discount_value: null, tax_rate_pct: 0, tax_label: null, deposit_pct: 30 });
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null);

  // Modification : caler l'éditeur sur le devis chargé, une seule fois. C'est un
  // ajustement d'état pendant le rendu (motif recommandé par React) et non un
  // effet : l'écran s'affiche directement avec les bonnes valeurs, sans clignoter.
  const loaded = existing.data;
  if (loaded && loadedFrom !== loaded.id && loaded.status === "draft") {
    setClientId(loaded.client_id);
    setHeader({ client_id: loaded.client_id, contact_id: loaded.contact_id, title: loaded.title ?? "", issue_date: loaded.issue_date, valid_until: loaded.valid_until, payment_days: loaded.payment_days, tax_regime_id: loaded.tax_regime_id, tax_label: loaded.tax_label, tax_rate_pct: Number(loaded.tax_rate_pct), tax_mention: loaded.tax_mention, notes_client: loaded.notes_client ?? "", notes_internal: loaded.notes_internal ?? "", terms: (loaded.terms as Record<string, unknown>) ?? {} });
    setDoc(quoteToDoc(loaded));
    setLoadedFrom(loaded.id);
  }

  // Un devis qui n'est plus un brouillon ne s'édite pas : on renvoie à sa fiche.
  // La navigation, elle, est bien une action : elle reste dans un effet.
  useEffect(() => {
    if (existing.data && existing.data.status !== "draft") nav(`/devis/${existing.data.id}`, { replace: true });
  }, [existing.data, nav]);

  // Création : appliquer les conditions du client, une fois qu'elles sont toutes
  // arrivées. Une seule application par client : sinon une requête lente écrasait
  // ensuite ce que l'utilisateur venait de modifier à la main.
  const [appliedClient, setAppliedClient] = useState<string | null>(null);
  if (!id && client && regimes && contacts && settings !== undefined && appliedClient !== client.id) {
    const regime = regimes.find((r) => r.id === client.tax_regime_id) ?? regimes.find((r) => r.is_default);
    setAppliedClient(client.id);
    setHeader((h) => ({
      ...h, client_id: client.id, payment_days: client.payment_days,
      tax_regime_id: regime?.id ?? null, tax_label: regime?.label ?? null, tax_rate_pct: regime ? Number(regime.rate_pct) : 0, tax_mention: regime?.mention ?? null,
      valid_until: h.valid_until ?? plusDays(h.issue_date, settings?.quote_validity_days ?? 30),
      contact_id: h.contact_id ?? contacts?.find((c) => c.is_primary)?.id ?? null,
    }));
    setDoc((d) => ({ ...d, currency: client.currency, deposit_pct: Number(client.deposit_pct), tax_rate_pct: regime ? Number(regime.rate_pct) : 0, tax_label: regime?.label ?? null }));
  }

  const canSave = useMemo(() => Boolean(clientId) && doc.lines.length > 0 && doc.lines.every((l) => l.label.trim() && l.quantity > 0 && l.unit_price >= 0), [clientId, doc]);

  function setRegime(regimeId: string) {
    const r = regimes?.find((x) => x.id === regimeId);
    if (!r) return;
    setHeader((h) => ({ ...h, tax_regime_id: r.id, tax_label: r.label, tax_rate_pct: Number(r.rate_pct), tax_mention: r.mention }));
    setDoc((d) => ({ ...d, tax_rate_pct: Number(r.rate_pct), tax_label: r.label }));
  }

  async function submit() {
    if (!canSave) { toast.error("Choisis un client et complète chaque ligne (libellé, quantité, prix)."); return; }
    const saved = await save.mutateAsync({ id, header: { ...header, client_id: clientId, title: header.title || null, notes_client: header.notes_client || null, notes_internal: header.notes_internal || null }, doc });
    toast.success(id ? "Brouillon enregistré" : "Devis créé");
    nav(`/devis/${saved}`);
  }

  return (
    <div>
      <Link to={id ? `/devis/${id}` : "/devis"} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> {id ? "Retour au devis" : "Devis"}</Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">{id ? `Modifier le brouillon${existing.data?.version ? ` — version ${existing.data.version}` : ""}` : "Nouveau devis"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Les prix viennent du catalogue ou du tarif négocié du client ; tout se recalcule à chaque modification.</p>
        </div>
        <Button onClick={() => void submit()} disabled={save.isPending || !canSave}><Save className="mr-2 h-4 w-4" />{id ? "Enregistrer le brouillon" : "Créer le devis"}</Button>
      </div>

      <section className="glass mb-6 rounded-2xl p-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="q-client">Client *</Label>
            <Select value={clientId} onValueChange={setClientId} disabled={Boolean(id)}>
              <SelectTrigger id="q-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>{clientsList?.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-contact">Interlocuteur</Label>
            <Select value={header.contact_id ?? "__none"} onValueChange={(v) => setHeader({ ...header, contact_id: v === "__none" ? null : v })} disabled={!clientId}>
              <SelectTrigger id="q-contact"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="__none">—</SelectItem>{contacts?.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name ?? ""}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="q-title">Objet</Label><Input id="q-title" value={header.title ?? ""} onChange={(e) => setHeader({ ...header, title: e.target.value })} placeholder="Ex. Leads plomberie — septembre" /></div>
          <div className="space-y-1.5">
            <Label htmlFor="q-currency">Devise</Label>
            <Select value={doc.currency} onValueChange={(v) => setDoc({ ...doc, currency: v as "EUR" | "USD" })}>
              <SelectTrigger id="q-currency"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="q-issue">Date</Label><Input id="q-issue" type="date" value={header.issue_date} onChange={(e) => setHeader({ ...header, issue_date: e.target.value })} /></div>
          <div className="space-y-1.5"><Label htmlFor="q-valid">Valide jusqu'au</Label><Input id="q-valid" type="date" value={header.valid_until ?? ""} onChange={(e) => setHeader({ ...header, valid_until: e.target.value || null })} /></div>
          <div className="space-y-1.5"><Label htmlFor="q-days">Délai de paiement (jours)</Label><Input id="q-days" type="number" min="0" max="180" value={header.payment_days} onChange={(e) => setHeader({ ...header, payment_days: Number(e.target.value) || 0 })} /></div>
          <div className="space-y-1.5">
            <Label htmlFor="q-tax">Régime fiscal</Label>
            <Select value={header.tax_regime_id ?? ""} onValueChange={setRegime}>
              <SelectTrigger id="q-tax"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{regimes?.map((r) => <SelectItem key={r.id} value={r.id}>{r.label} — {Number(r.rate_pct)} %</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {clientId ? <DocumentEditor doc={doc} onChange={setDoc} clientId={clientId} /> : <p className="glass rounded-2xl p-6 text-sm text-muted-foreground">Choisis d'abord un client : ses tarifs négociés, sa devise, son acompte et son régime fiscal s'appliqueront.</p>}

      <section className="glass mt-6 grid gap-4 rounded-2xl p-5 md:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="q-notes">Notes pour le client (imprimées sur le devis)</Label><Textarea id="q-notes" rows={3} value={header.notes_client ?? ""} onChange={(e) => setHeader({ ...header, notes_client: e.target.value })} /></div>
        <div className="space-y-1.5"><Label htmlFor="q-internal">Notes internes</Label><Textarea id="q-internal" rows={3} value={header.notes_internal ?? ""} onChange={(e) => setHeader({ ...header, notes_internal: e.target.value })} /></div>
      </section>
      <div className="mt-6 flex justify-end">
        <Button onClick={() => void submit()} disabled={save.isPending || !canSave}><Save className="mr-2 h-4 w-4" />{id ? "Enregistrer le brouillon" : "Créer le devis"}</Button>
      </div>
    </div>
  );
}
